"use client";

import ChessTitle, { parseChessTitle } from "@/components/chess-title";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/shadcn/avatar";
import { CardTitle } from "@/components/shadcn/card";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { getFormattedImageUrl } from "@/lib/utils";
import type { VideoTrainer } from "@prisma/client";
import { Heart } from "lucide-react";
import { useTranslations } from "next-intl";
import { seriesActionClassName } from "../series-action";
import SeriesRating from "../series-rating";

type VideoDetailMetaProps = {
    title: string;
    videoTrainer: VideoTrainer;
    seriesId: number;
    bookmarkCount: number;
    isBookmarked: boolean;
    isLoggedIn: boolean;
    onLoginRequest: () => void;
    onBookmark: () => void;
};

export function VideoDetailMeta({
    title,
    videoTrainer,
    seriesId,
    bookmarkCount,
    isBookmarked,
    isLoggedIn,
    onLoginRequest,
    onBookmark,
}: VideoDetailMetaProps) {
    const t = useTranslations("series");
    const trainerTitle = parseChessTitle(videoTrainer.Title);
    const trainerName = `${videoTrainer.FirstName} ${videoTrainer.Lastname}`;
    const trainerInitials = `${videoTrainer.FirstName.charAt(0)}${videoTrainer.Lastname.charAt(0)}`;
    const trainerImage = getFormattedImageUrl(videoTrainer.PictureLocation);

    return (
        <div className="flex flex-col gap-4">
            <CardTitle>{title}</CardTitle>

            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="size-12">
                        {trainerImage && (
                            <AvatarImage src={trainerImage} alt={trainerName} />
                        )}
                        <AvatarFallback>{trainerInitials}</AvatarFallback>
                    </Avatar>
                    <span className="flex min-w-0 flex-col">
                        {trainerTitle && <ChessTitle title={trainerTitle} />}
                        <span>{trainerName}</span>
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <SeriesRating seriesId={seriesId} />
                    {!isLoggedIn ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className={seriesActionClassName}
                                    onClick={onLoginRequest}
                                >
                                    <Heart className="size-4" />
                                    {t("save")}
                                    {bookmarkCount > 0 && ` (${bookmarkCount})`}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>{t("loginToSave")}</TooltipContent>
                        </Tooltip>
                    ) : (
                        <button
                            type="button"
                            className={seriesActionClassName}
                            onClick={onBookmark}
                        >
                            <Heart
                                className="size-4"
                                fill={isBookmarked ? "currentColor" : "none"}
                            />
                            {t("save")}
                            {bookmarkCount > 0 && ` (${bookmarkCount})`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
